'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import {
  EditIcon,
  ShieldIcon,
  UsersIcon,
  AlertCircleIcon,
  CheckCircle2Icon,
  ArrowLeftIcon,
  TrashIcon,
  ShieldUserIcon
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Separator } from '@/components/ui/separator'
import { ContentDetailInfo, DeleteConfirmationDialog } from '@/components/partials'
import { useGroup, useDeleteGroup } from '@/hooks'

type PropsType = {
  id: string
}

const Detail = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const { data: group, isLoading, error } = useGroup(id)
  const { mutate: deleteGroup, isPending: isDeleting } = useDeleteGroup()

  const handleDelete = () => {
    deleteGroup(id, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['groups'] })
        toast.success(t('deleted_successfully'))
        router.push('/users/groups')
      },
      onError: (error: any) => {
        console.error('Error deleting group:', error)
        toast.error(error?.response?.data?.message || t('error_deleting_group'))
      }
    })
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (error || !group) {
    return (
      <Alert variant="destructive">
        <AlertCircleIcon className="h-4 w-4" />
        <AlertDescription>{t('error_loading_record')}</AlertDescription>
      </Alert>
    )
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="grid grid-cols-1 items-center justify-between gap-2 sm:grid-cols-2">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" asChild>
              <Link href="/users/groups">
                <ArrowLeftIcon className="h-4 w-4" />
                <span className="hidden lg:flex">{t('list')}</span>
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">{t('group_details')}</h1>
              <p className="text-muted-foreground">{t('view_group_details')}</p>
            </div>
          </div>
          <div className="flex flex-row items-center justify-end gap-2">
            <Button variant="outline" asChild>
              <Link href={`/users/groups/${id}/edit`}>
                <EditIcon className="h-4 w-4" />
                <span className="hidden lg:flex">{t('edit_user_group')}</span>
              </Link>
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <TrashIcon className="h-4 w-4" />
              <span className="hidden lg:flex">{t('delete')}</span>
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldUserIcon className="h-5 w-5" />
              {t('information')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start justify-items-center space-x-2">
                <ShieldIcon className="text-muted-foreground h-5 w-5" />
                <div>
                  <div className="text-muted-foreground text-sm">{t('role')}</div>
                  <Badge variant="outline">{t(group.role)}</Badge>
                </div>
              </div>

              <div className="flex items-start justify-items-center space-x-2">
                <UsersIcon className="text-muted-foreground h-5 w-5" />
                <div>
                  <div className="text-muted-foreground text-sm">{t('users')}</div>
                  <div className="font-medium">{group.userCount}</div>
                </div>
              </div>
            </div>

            <Separator />

            <ContentDetailInfo entity={group} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('permissions')}</CardTitle>
            <CardDescription>{t('assigned_permissions_for_group')}</CardDescription>
          </CardHeader>
          <CardContent>
            {group.permissions && group.permissions.length > 0 ? (
              <div className="space-y-4">
                {group.permissions.map((permission: any) => (
                  <div key={permission.id} className="rounded-lg border p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <h3 className="font-semibold">{t(permission.name.toLowerCase())}</h3>
                    </div>
                    <Separator className="mb-3" />
                    <div className="grid grid-cols-2 gap-2 lg:grid-cols-4 xl:grid-cols-5">
                      {permission.actions.map((action: any) => (
                        <div key={action.id} className="flex items-center justify-items-start space-x-2">
                          <CheckCircle2Icon className="h-4 w-4 text-green-500" />
                          <span className="text-sm">{t(`permission_actions_${action.name.toLowerCase()}`)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Alert>
                <AlertCircleIcon className="h-4 w-4" />
                <AlertDescription>{t('no_permissions_assigned')}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>

      <DeleteConfirmationDialog
        open={deleteConfirmOpen}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
    </>
  )
}

export default Detail
